import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { CheckCircle, User, Mail, Lock, Building, Phone, MapPin, ArrowRight, Loader2, Briefcase, Globe } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERTISE_OPTIONS = [
  'Medical & Healthcare',
  'Education',
  'Humanitarian Aid',
  'Food Distribution',
  'Shelter & Housing',
  'Construction',
  'WASH (Water, Sanitation, Hygiene)',
  'Monitoring & Evaluation',
  'Community Development',
  'Agriculture',
  'Livelihood Programs',
  'Child Protection',
  'Gender-Based Violence',
  'Emergency Response',
  'Other'
];

const SECTOR_OPTIONS = [
  'Health',
  'Education',
  'Food Security',
  'WASH',
  'Shelter',
  'Protection',
  'Livelihoods',
  'Nutrition',
  'Camp Management',
  'Early Recovery'
];

export default function RegisterAgentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    country: '',
    years_of_experience: '',
    field_of_expertise: '',
    sector_specialization: [],
    previous_projects: '',
    languages: '',
    availability: 'full_time',
    certifications: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSectorChange = (sector) => {
    const current = formData.sector_specialization;
    if (current.includes(sector)) {
      setFormData({
        ...formData,
        sector_specialization: current.filter(s => s !== sector)
      });
    } else {
      setFormData({
        ...formData,
        sector_specialization: [...current, sector]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone || null,
        country: formData.country || null,
        years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
        field_of_expertise: formData.field_of_expertise || null,
        sector_specialization: formData.sector_specialization,
        previous_projects: formData.previous_projects || null,
        languages: formData.languages ? formData.languages.split(',').map(l => l.trim()) : [],
        availability: formData.availability || null,
        certifications: formData.certifications || null
      };

      await axios.post(`${API}/auth/register/agent`, payload);
      
      toast.success('Registration submitted! Please wait for admin approval before you can login.');
      navigate('/login');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_80a6bcee-7228-4b60-b359-6ff952ed0f3c/artifacts/ufoinc0k_logo1-removebg-preview.png" 
            alt="Verify Logo" 
            className="h-48 w-auto"
          />
        </Link>

        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold font-['Manrope']">Field Agent Registration</CardTitle>
            <CardDescription>
              Complete your professional profile. Your account will require admin approval before access.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="pl-10"
                        required
                        data-testid="agent-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="pl-10"
                        required
                        data-testid="agent-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 123-4567"
                        className="pl-10"
                        data-testid="agent-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country / Area</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder="Kenya"
                        className="pl-10"
                        data-testid="agent-country-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Profile */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">Professional Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="years_of_experience">Years of Experience</Label>
                    <Input
                      id="years_of_experience"
                      name="years_of_experience"
                      type="number"
                      min="0"
                      value={formData.years_of_experience}
                      onChange={handleChange}
                      placeholder="5"
                      data-testid="agent-experience-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="field_of_expertise">Field of Expertise</Label>
                    <Select
                      value={formData.field_of_expertise}
                      onValueChange={(v) => setFormData({ ...formData, field_of_expertise: v })}
                    >
                      <SelectTrigger data-testid="agent-expertise-select">
                        <SelectValue placeholder="Select expertise" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPERTISE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label>Sector Specialization (select all that apply)</Label>
                  <div className="flex flex-wrap gap-2">
                    {SECTOR_OPTIONS.map((sector) => (
                      <button
                        key={sector}
                        type="button"
                        onClick={() => handleSectorChange(sector)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          formData.sector_specialization.includes(sector)
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                        }`}
                      >
                        {sector}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label htmlFor="previous_projects">Previous Project Experience</Label>
                  <Textarea
                    id="previous_projects"
                    name="previous_projects"
                    value={formData.previous_projects}
                    onChange={handleChange}
                    placeholder="Describe your previous M&E or field work experience..."
                    rows={3}
                    data-testid="agent-projects-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="languages">Languages Spoken (comma-separated)</Label>
                    <Input
                      id="languages"
                      name="languages"
                      value={formData.languages}
                      onChange={handleChange}
                      placeholder="English, French, Swahili"
                      data-testid="agent-languages-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="availability">Availability</Label>
                    <Select
                      value={formData.availability}
                      onValueChange={(v) => setFormData({ ...formData, availability: v })}
                    >
                      <SelectTrigger data-testid="agent-availability-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full-time</SelectItem>
                        <SelectItem value="part_time">Part-time</SelectItem>
                        <SelectItem value="contract">Contract/Project-based</SelectItem>
                        <SelectItem value="freelance">Freelance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label htmlFor="certifications">Certifications (optional)</Label>
                  <Input
                    id="certifications"
                    name="certifications"
                    value={formData.certifications}
                    onChange={handleChange}
                    placeholder="M&E Certification, Project Management, etc."
                    data-testid="agent-certifications-input"
                  />
                </div>
              </div>

              {/* Account Security */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">Account Security</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Create a password"
                        className="pl-10"
                        required
                        data-testid="agent-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm password"
                        className="pl-10"
                        required
                        data-testid="agent-confirm-password-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Your account will be reviewed by an administrator. 
                  You will receive access once your profile has been approved.
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="agent-submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Registration
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-slate-900 font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
